import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  PublishNewActivitiesVersionMutation,
  PublishNewActivitiesVersionMutationVariables,
} from './settings-publish.graphql.generated';
import type {
  UpdateExistingActivitiesVersionMutation,
  UpdateExistingActivitiesVersionMutationVariables,
} from './settings-publish.graphql.generated';
import type {
  CreatePublishTaskMutation,
  CreatePublishTaskMutationVariables,
} from './settings-publish.graphql.generated';
import type {
  GetPublishTaskByIdQuery,
  GetPublishTaskByIdQueryVariables,
} from './settings-publish.graphql.generated';
import {
  ASSESSMENT_ACTIVITIES,
  LANGUAGE_CODE_EN_AU,
  LANGUAGE_CODE_ES_US,
  LanguageCodes,
  PUBLISH_TASK_STATUS_PDF_GENERATED,
} from 'global/constants';
import { InquisitiveActivitiesContentType } from 'global/graphql/scalars.type';
import { useLoggingMutation, useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { getServerErrorMessage } from 'lib/apollo.utils';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import { Box } from 'approot/shared/design-system/box/box.style';
import Text from 'approot/shared/styles/typography/text';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { openInWindow } from 'approot/shared/content-card/content-card.utils';
import { getInteractiveContentLabel } from '../../activity-builder.utils';
import { ActivityBuilderModal } from '../../activity-builder-modal';
import {
  DraftAssessmentActivities,
  DraftLessonActivities,
} from 'approot/activity-builder/activity-builder-draft.constants';
import {
  CREATE_PUBLISH_TASK,
  GET_PUBLISH_TASK_BY_ID,
  PUBLISH_NEW_ACTIVITIES_VERSION,
  UPDATE_EXISTING_ACTIVITIES_VERSION,
} from './settings-publish.graphql';
import SettingsGeneratePrintContent from './settings-generate-print-content';
import useTranslateActivities from 'approot/activity-builder/lang/use-translate-activities';
import useGetActivitiesChanges from './use-get-activities-changes';
import { removeTypename } from 'approot/shared/logic/remove-typename';
import { updateActivitiesStatusInCache } from './logic/update-activities-status-in-cache';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';

const TRANSLATED_LANGUAGES: LanguageCodes[] = [LANGUAGE_CODE_ES_US];

const PRINT_LANGUAGES: LanguageCodes[] = [
  LANGUAGE_CODE_EN_AU,
  LANGUAGE_CODE_ES_US,
];

type PrintJob = {
  language: LanguageCodes;
  uploadUrl: string;
  version: string;
  hasGenerated: boolean;
};

type Props = {
  contentId: number;
  contentType: InquisitiveActivitiesContentType;
  activities: DraftLessonActivities | DraftAssessmentActivities;
  createNewVersion: boolean;
  description: string;
  isAutomarkable?: boolean;
  onClose: () => void;
};

const SettingsPublishModal = ({
  contentId,
  contentType,
  activities,
  createNewVersion,
  description,
  isAutomarkable,
  onClose,
}: Props) => {
  const { userClassId } = useGetInteractiveLessonParams();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRunningJobs, setIsRunningJobs] = useState<boolean>(false);
  const [defaultLanguage, setDefaultLanguage] = useState<PrintJob | null>(null);
  const [otherLanguages, setOtherLanguages] = useState<PrintJob[] | null>(null);

  const [pollInterval, setPollInterval] = useState<number>(0);

  const { showError, showSuccess } = useSnackbar();

  const contentTypeLabel = useMemo(
    () => getInteractiveContentLabel(contentType),
    [contentType]
  );

  const { data: getActivitiesChangesData } = useGetActivitiesChanges({
    contentId,
    contentType,
  });

  const [publish, { loading: publishLoading, client }] = useLoggingMutation<
    PublishNewActivitiesVersionMutation,
    PublishNewActivitiesVersionMutationVariables
  >(PUBLISH_NEW_ACTIVITIES_VERSION, {
    onError: () => {
      onClose();
      showError(`There was an error publishing the ${contentTypeLabel}`);
    },
  });

  const { translate, loading: translateLoading } = useTranslateActivities();

  const [update, { loading: patchLoading }] = useLoggingMutation<
    UpdateExistingActivitiesVersionMutation,
    UpdateExistingActivitiesVersionMutationVariables
  >(UPDATE_EXISTING_ACTIVITIES_VERSION, {
    onError: error => {
      onClose();
      showError(
        getServerErrorMessage(error) ??
          `Oops! Something went wrong with publishing your ${contentTypeLabel}. Please try again.`
      );
    },
  });

  const [createPublishTask, { data: createPublishTaskData }] =
    useLoggingMutation<
      CreatePublishTaskMutation,
      CreatePublishTaskMutationVariables
    >(CREATE_PUBLISH_TASK, {
      fetchPolicy: 'network-only',
      onError: error => {
        handlePublishEnd();
        onClose();
        showError(
          getServerErrorMessage(error) ??
            `Oops! Something went wrong with publishing your ${contentTypeLabel}. Please try again.`
        );
      },
    });

  const publishTaskId = createPublishTaskData?.createPublishTask.id;

  const { data: getTaskByIdData } = useLoggingQuery<
    GetPublishTaskByIdQuery,
    GetPublishTaskByIdQueryVariables
  >(GET_PUBLISH_TASK_BY_ID, {
    skip: !publishTaskId,
    pollInterval: pollInterval,
    fetchPolicy: 'network-only',
    variables: {
      id: publishTaskId!,
    },
    onCompleted: res => {
      if (res.getPublishTaskById.status === PUBLISH_TASK_STATUS_PDF_GENERATED) {
        handlePublishEnd();
      }
    },
    onError: () => {
      handlePublishEnd();
      onClose();
      showError(
        `Oops! Something went wrong with publishing your ${contentTypeLabel}. Please try again.`
      );
    },
  });

  const downloadUrl =
    getTaskByIdData?.getPublishTaskById.assetDownloadUrls.find(
      d => d.language === LANGUAGE_CODE_EN_AU
    )?.url;

  const handlePublishEnd = useCallback(() => {
    setPollInterval(0);
    setIsLoading(false);
  }, [setPollInterval, setIsLoading]);

  const handlePublishStart = useCallback(
    ({ createNewVersion }: { createNewVersion: boolean }) => {
      createPublishTask({
        variables: {
          input: {
            contentId,
            contentType,
            createNewVersion,
            languages: PRINT_LANGUAGES,
            majorVersionDescription: description,
            ...(contentType === ASSESSMENT_ACTIVITIES
              ? { isAutomarkable }
              : {}),
          },
        },
      })
        .then(res => {
          const uploadUrls = res.data?.createPublishTask.uploadUrls;
          const uploadUrl = uploadUrls?.[0].url;
          const version = res.data?.createPublishTask.version;
          if (res.data && uploadUrl && version) {
            setDefaultLanguage({
              language: LANGUAGE_CODE_EN_AU,
              uploadUrl: uploadUrl,
              version,
              hasGenerated: false,
            });

            // map the uploadUrls to the setOtherLanguages excluding en-us
            const otherLanguages = uploadUrls
              .filter(l => l.language !== LANGUAGE_CODE_EN_AU)
              .map(l => ({
                language: l.language,
                uploadUrl: l.url,
                version,
                hasGenerated: false,
              }));

            setOtherLanguages(() => otherLanguages);
            setPollInterval(3000);
          }
        })
        .catch(() => {
          handlePublishEnd();
        });
    },
    [
      contentId,
      contentType,
      setOtherLanguages,
      setDefaultLanguage,
      createPublishTask,
      handlePublishEnd,
      description,
      isAutomarkable,
    ]
  );

  useEffect(() => {
    handlePublishStart({
      createNewVersion,
    });
  }, [createNewVersion, handlePublishStart]);

  const nextLanguageJob = useMemo(
    () => (isRunningJobs ? otherLanguages?.find(l => !l.hasGenerated) : null),
    [isRunningJobs, otherLanguages]
  );

  const defaultLanguageContent = useMemo(
    () =>
      defaultLanguage && !defaultLanguage.hasGenerated ? (
        <SettingsGeneratePrintContent
          contentId={contentId}
          contentType={contentType}
          classId={userClassId}
          language={defaultLanguage.language}
          version={defaultLanguage.version}
          onRenderComplete={async (contentString: string) => {
            await fetch(defaultLanguage.uploadUrl, {
              method: 'PUT',
              body: contentString,
            });
            setDefaultLanguage({
              ...defaultLanguage,
              hasGenerated: true,
            });
          }}
        />
      ) : null,
    [defaultLanguage, contentType, contentId, userClassId]
  );

  const translatedLanguageContent = useMemo(
    () =>
      nextLanguageJob ? (
        <SettingsGeneratePrintContent
          contentId={contentId}
          contentType={contentType}
          classId={userClassId}
          version={nextLanguageJob.version}
          language={nextLanguageJob.language}
          onRenderComplete={async (contentString: string) => {
            await fetch(nextLanguageJob.uploadUrl, {
              method: 'PUT',
              body: contentString,
            });

            // set hasGenerated to true
            const updatedOtherLanguages =
              otherLanguages?.map(l =>
                l.language === nextLanguageJob.language
                  ? { ...l, hasGenerated: true }
                  : l
              ) || [];

            // has more jobs to run
            if (updatedOtherLanguages?.every(l => l.hasGenerated)) {
              setIsRunningJobs(false);
              showSuccess(`${contentTypeLabel} successfully published 🎉`);
              onClose();
            }

            // this should trigger a re-render and the next job will be rendered
            setOtherLanguages(() => updatedOtherLanguages);
          }}
        />
      ) : null,
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [
      nextLanguageJob,
      contentTypeLabel,
      activities,
      contentType,
      contentId,
      onClose,
      showSuccess,
    ]
  );

  const handlePublish = useCallback(async () => {
    if (publishTaskId) {
      if (createNewVersion) {
        const res = await publish({
          variables: {
            input: {
              publishTaskId,
            },
          },
        });
        if (res.data?.publishNewActivitiesVersion.version) {
          updateActivitiesStatusInCache({
            client,
            typename: res.data?.publishNewActivitiesVersion.__typename,
            contentId,
          });

          await translate({
            variables: {
              input: {
                contentType,
                languages:
                  window.location.hostname === 'localhost'
                    ? []
                    : TRANSLATED_LANGUAGES,
                version: res.data.publishNewActivitiesVersion.version,
              },
            },
          });
          setIsRunningJobs(true);
        }
      } else {
        const res = await update({
          variables: {
            input: {
              publishTaskId,
              changes: removeTypename(
                getActivitiesChangesData?.getActivitiesChanges || []
              ),
            },
          },
        });
        if (res.data?.updateExistingActivitiesVersion.version) {
          updateActivitiesStatusInCache({
            client,
            typename: res.data?.updateExistingActivitiesVersion.__typename,
            contentId,
          });
          await translate({
            variables: {
              input: {
                contentType,
                languages:
                  window.location.hostname === 'localhost'
                    ? []
                    : TRANSLATED_LANGUAGES,
                version: res.data.updateExistingActivitiesVersion.version,
              },
            },
          });
          setIsRunningJobs(true);
        }
      }
    }
  }, [
    client,
    contentId,
    contentType,
    createNewVersion,
    publishTaskId,
    getActivitiesChangesData?.getActivitiesChanges,
    publish,
    update,
    translate,
  ]);

  return (
    <>
      <ActivityBuilderModal
        isVisible
        title={`Publishing ${contentTypeLabel}`}
        headerTitle={`Publishing ${contentTypeLabel}`}
        actions={
          isLoading ? null : (
            <>
              <PrimaryButton
                data-selector="btn-publish"
                type="button"
                disabled={
                  !downloadUrl ||
                  patchLoading ||
                  publishLoading ||
                  translateLoading ||
                  !!nextLanguageJob
                }
                loading={
                  patchLoading ||
                  publishLoading ||
                  translateLoading ||
                  !!nextLanguageJob
                }
                onClick={handlePublish}
              >
                Publish
              </PrimaryButton>
              <SecondaryButton
                data-selector="btn-download-pdf"
                type="button"
                disabled={!downloadUrl}
                onClick={() => {
                  if (downloadUrl) {
                    openInWindow(downloadUrl);
                  }
                }}
              >
                Download PDF
              </SecondaryButton>
            </>
          )
        }
        onClose={() => {
          onClose();
          handlePublishEnd();
        }}
      >
        {!downloadUrl && (
          <>
            <Text $marginBottom={16}>
              Getting ready to publish. First we need to generate the{' '}
              {contentTypeLabel} PDF.
            </Text>
            <Box $marginBottom={16}>
              <Bouncer />
            </Box>
          </>
        )}
        {downloadUrl && (
          <Text>
            PDF Generated. Please check your PDF and publish when you’re ready.
          </Text>
        )}
      </ActivityBuilderModal>

      {defaultLanguageContent}
      {translatedLanguageContent}
    </>
  );
};

export default SettingsPublishModal;
